import React, { useEffect, useState } from "react";
import CustomDialog from "../../components/layouts/common/CustomDialog";
import CustomInput from "../../components/inputs/CustomInputs";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItem,
  OutlinedInput,
  TextField,
} from "@mui/material";
import { LOG_TYPE, MATERIAL_TYPE } from "../../utils/constants";
import { findObjectKeyByValue, titleCase } from "../../utils/main";
import moment from "moment";
import { fetchRawMaterialApi } from "../../apis/rawMaterial.api";
import AsyncDropDown from "../../components/inputs/AsyncDropDown";
import { StyledSearchBar } from "../../components/inputs/SearchBar";
import { FetchSKDApi } from "../../apis/skd.api";
import { FetchSFGApi } from "../../apis/sfg.api";
import { FetchFGApi } from "../../apis/FG.api";
import { CenteredBox } from "../../components/layouts/OneViewBox";
import PaddingBoxInDesktop from "../../components/layouts/PaddingBoxDesktop";
import { fetchCategoryApi } from "../../apis/category.api";
import { useDispatch } from "react-redux";
import { callApiAction } from "../../store/actions/commonAction";
import { fetchHrmsProjects } from "../../apis/item.api";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const PurchaseRequestCreateUi = ({
  id,
  loading,
  onSubmit,
  fields,
  setFields,
  isview,
}) => {
  let materialtype = [
    ...Object.keys(MATERIAL_TYPE)?.map((key) => ({
      label: titleCase(key),
      _id: MATERIAL_TYPE[key],
    })),
  ];
  const [project, SetProject] = useState([]);
  const dispatch = useDispatch();
  const [showOtherFields, setShowOtherFields] = useState(false);

  // Total items count (1 default + additionalItems)
  const totalItemsCount = 1 + (fields?.additionalItems?.length || 0);

  // Delete button should be disabled when only 1 item exists
  const isDeleteDisabled = totalItemsCount <= 1;

  const fetchProductList = () => {
    dispatch(
      callApiAction(
        async () =>
          await fetchHrmsProjects({
            allStatus: true,
            search: fields?.projectNames,
            searchable: ["project_name"],
          }),
        (response) => {
          const uniqueProjects = [
            ...new Map(
              response?.result.map((item) => [item?.project_name, item]),
            ).values(),
          ];
          SetProject(uniqueProjects);
        },
        (err) => {
          console.log("Fetch error", err);
        },
      ),
    );
  };

  useEffect(() => {
    fetchProductList();
  }, [fields?.projectNames]);

  const handleAddItem = () => {
    const newItem = {
      materialType:
        fields.additionalItems[fields.additionalItems.length - 1]
          ?.materialType || 1,
      materialId: null,
      materialName: null,
      materialname: null,
      quantity: "",
      reason: "",
      name: "",
      link: "",
      showOtherFields: false,
    };

    setFields({
      ...fields,
      additionalItems: [...(fields.additionalItems || []), newItem],
    });
  };

  // Remove default item - shift first additional item to default
  const handleRemoveDefaultItem = () => {
    if (fields?.additionalItems?.length > 0) {
      const firstAdditional = fields.additionalItems[0];
      const remainingItems = fields.additionalItems.slice(1);

      setFields({
        ...fields,
        materialType: firstAdditional.materialType,
        materialId: firstAdditional.materialId,
        materialName: firstAdditional.materialName,
        materialname: firstAdditional.materialname,
        quantity: firstAdditional.quantity,
        reason: firstAdditional.reason,
        name: firstAdditional.name,
        link: firstAdditional.link,
        additionalItems: remainingItems,
      });
      setShowOtherFields(firstAdditional.showOtherFields || false);
    }
  };

  const handleRemoveItem = (index) => {
    const updatedItems = fields.additionalItems.filter((_, i) => i !== index);
    setFields({
      ...fields,
      additionalItems: updatedItems,
    });
  };

  const updateAdditionalItem = (index, updatedData) => {
    const updatedItems = fields.additionalItems.map((item, i) =>
      i === index ? { ...item, ...updatedData } : item,
    );
    setFields({
      ...fields,
      additionalItems: updatedItems,
    });
  };

  return (
    <CustomDialog
      id={"purchase"}
      loading={loading}
      err={fields.err}
      onSubmit={onSubmit}
      title={isview ? "View Material Request" : "Material Request"}
      closeText={"Close"}
      confirmText={isview ? undefined : "Create"}
    >
      {loading && (
        <CenteredBox>
          <CircularProgress />
        </CenteredBox>
      )}

      {/*  CATEGORY & PROJECT  */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
          mt: 1,
        }}
      >
        {/* Category */}
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <AsyncDropDown
            defaultVal={
              fields.categoryId
                ? {
                    _id: fields.categoryId._id,
                    name: fields.categoryId.name,
                  }
                : null
            }
            lazyFun={async (para) =>
              await fetchCategoryApi({ ...para, allStatus: true })
            }
            OptionComponent={({ option, ...rest }) => {
              return <ListItem {...rest}>{option.name}</ListItem>;
            }}
            value={fields?.categoryId}
            onChange={async (changedVal) => {
              setFields({
                ...fields,
                categoryId: changedVal ? changedVal._id : null,
                categoryName: changedVal ? changedVal.name : null,
              });
            }}
            titleKey={"name"}
            valueKey={"_id"}
            InputComponent={(params) => (
              <StyledSearchBar
                placeholder={"Select Category*"}
                {...params}
                margin="none"
                disabled={isview}
              />
            )}
            disabled={isview}
          />
        </Box>

        {fields?.categoryName === "R & D" && (
          <Box sx={{ flex: 1, minWidth: 200, mb: 1 }}>
            <Autocomplete
              value={project.find((p) => p._id === fields?.projectId) || null}
              sx={{ width: "100%" }}
              options={project ?? []}
              getOptionLabel={(option) => option.project_name}
              onChange={(event, value) => {
                setFields({
                  ...fields,
                  projectId: value ? value._id : null,
                  projectName: value ? value.project_name : null,
                });
              }}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    label={option.project_name}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  onChange={(e) =>
                    setFields({
                      ...fields,
                      err: "",
                      projectNames: e.target.value,
                    })
                  }
                  margin="dense"
                  label="Select Project*"
                  variant="outlined"
                  placeholder="Select Project*"
                />
              )}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ border: "1px solid", borderRadius: 2, p: 2, mt: 2 }}>
        {/* DEFAULT MATERIAL FIELDS */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {/* Material Type */}
          <Box sx={{ flex: 1, minWidth: 150 }}>
            <Autocomplete
              disableClearable
              options={materialtype}
              value={
                materialtype.find(
                  (item) => item._id === fields?.materialType,
                ) || null
              }
              onChange={(e, newVal) => {
                const selectedType = newVal ? newVal._id : null;

                setFields((prev) => ({
                  ...prev,
                  materialType: selectedType,
                  materialId: null,
                  materialName: null,
                  additionalItems: (prev.additionalItems || []).map((item) => ({
                    ...item,
                    materialType: selectedType,
                    materialId: null,
                    materialName: null,
                    materialname: null,
                    showOtherFields: false,
                  })),
                }));

                setShowOtherFields(false);
              }}
              sx={{ width: "100%" }}
              renderInput={(params) => (
                <CustomInput
                  placeholder="Select Material Type*"
                  {...params}
                  label="Select Material Type*"
                  margin="dense"
                  disabled={isview}
                />
              )}
              disabled={isview}
            />
          </Box>

          {/* Material Selection */}
          {fields?.materialType && (
            <Box sx={{ flex: 1, minWidth: 150 }}>
              {fields?.materialType == 1 && (
                <AsyncDropDown
                  Value={
                    fields?.materialName
                      ? {
                          _id: fields?.materialIds?._id,
                          name: fields?.materialName,
                        }
                      : null
                  }
                  defaultOptions={[{ name: "Other", _id: "other" }]}
                  lazyFun={async (para) =>
                    await fetchRawMaterialApi({ ...para, allStatus: true })
                  }
                  OptionComponent={({ option, ...rest }) => {
                    return (
                      <ListItem {...rest}>
                        {option?.name} {option?.mpn ? `[${option?.mpn}]` : ""}
                      </ListItem>
                    );
                  }}
                  onChange={async (changedVal) => {
                    if (changedVal && changedVal._id === "other") {
                      setShowOtherFields(true);
                    } else {
                      setShowOtherFields(false);
                    }
                    setFields({
                      ...fields,
                      materialId: changedVal ? changedVal?._id : null,
                      materialName: changedVal
                        ? `${changedVal?.name ?? ""}${changedVal?.mpn ? ` [${changedVal?.mpn}]` : ""}`.trim()
                        : null,
                    });
                  }}
                  titleKey={"name"}
                  valueKey={"_id"}
                  InputComponent={(params) => (
                    <CustomInput
                      {...params}
                      label="Select Raw Material*"
                      placeholder="Select Raw Material*"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      disabled={isview}
                    />
                  )}
                  disabled={isview}
                />
              )}

              {fields?.materialType === 2 && (
                <AsyncDropDown
                  defaultVal={
                    fields?.materialId
                      ? {
                          _id: fields?.materialId?._id
                            ? fields?.materialId?._id
                            : fields?.materialId,
                          name: fields?.materialId?.name
                            ? fields?.materialId?.name
                            : fields?.materialname,
                        }
                      : null
                  }
                  defaultOptions={[{ name: "Other", _id: "other" }]}
                  lazyFun={async (para) =>
                    await FetchSKDApi({ ...para, allStatus: true })
                  }
                  OptionComponent={({ option, ...rest }) => {
                    return <ListItem {...rest}>{option?.name}</ListItem>;
                  }}
                  value={fields?.materialname}
                  onChange={async (changedVal) => {
                    if (changedVal && changedVal._id === "other") {
                      setShowOtherFields(true);
                    } else {
                      setShowOtherFields(false);
                    }
                    setFields({
                      ...fields,
                      materialId: changedVal ? changedVal?._id : null,
                      materialname: changedVal ? changedVal?.name : null,
                    });
                  }}
                  titleKey={"name"}
                  valueKey={"_id"}
                  InputComponent={(params) => (
                    <CustomInput
                      {...params}
                      label="Select Semi-Knocked Down (SKD)*"
                      placeholder="Select Semi-Knocked Down (SKD)*"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      disabled={isview}
                    />
                  )}
                  disabled={isview}
                />
              )}

              {fields?.materialType === 3 && (
                <AsyncDropDown
                  Value={
                    fields?.materialName
                      ? {
                          _id: fields?.materialId?._id
                            ? fields?.materialId?._id
                            : fields?.materialId,
                          name: fields?.materialName,
                        }
                      : null
                  }
                  defaultOptions={[{ name: "Other", _id: "other" }]}
                  lazyFun={async (para) =>
                    await FetchSFGApi({ ...para, allStatus: true })
                  }
                  OptionComponent={({ option, ...rest }) => {
                    return (
                      <ListItem {...rest}>
                        {option?.name} {option?.mpn ? `[${option?.mpn}]` : ""}
                      </ListItem>
                    );
                  }}
                  onChange={async (changedVal) => {
                    if (changedVal && changedVal._id === "other") {
                      setShowOtherFields(true);
                    } else {
                      setShowOtherFields(false);
                    }
                    setFields({
                      ...fields,
                      materialId: changedVal ? changedVal?._id : null,
                      materialName: changedVal
                        ? `${changedVal?.name ?? ""}${changedVal?.mpn ? ` [${changedVal?.mpn}]` : ""}`.trim()
                        : null,
                    });
                  }}
                  titleKey={"name"}
                  valueKey={"_id"}
                  InputComponent={(params) => (
                    <CustomInput
                      {...params}
                      label="Select Semi-Finished Goods (SFG)*"
                      placeholder="Select Semi-Finished Goods (SFG)*"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      disabled={isview}
                    />
                  )}
                  disabled={isview}
                />
              )}

              {fields?.materialType == 4 && (
                <AsyncDropDown
                  defaultVal={
                    fields?.materialId
                      ? {
                          _id: fields?.materialId?._id
                            ? fields?.materialId?._id
                            : fields?.materialId,
                          name: fields?.materialId?.name
                            ? fields?.materialId?.name
                            : fields?.materialname,
                        }
                      : null
                  }
                  defaultOptions={[{ name: "Other", _id: "other" }]}
                  lazyFun={async (para) =>
                    await FetchFGApi({ ...para, allStatus: true })
                  }
                  OptionComponent={({ option, ...rest }) => {
                    return <ListItem {...rest}>{option?.name}</ListItem>;
                  }}
                  value={fields?.materialname}
                  onChange={async (changedVal) => {
                    if (changedVal && changedVal._id === "other") {
                      setShowOtherFields(true);
                    } else {
                      setShowOtherFields(false);
                    }
                    setFields({
                      ...fields,
                      materialId: changedVal ? changedVal?._id : null,
                      materialname: changedVal ? changedVal?.name : null,
                    });
                  }}
                  titleKey={"name"}
                  valueKey={"_id"}
                  InputComponent={(params) => (
                    <CustomInput
                      {...params}
                      label="Select Finished Goods (FG)*"
                      placeholder="Select Finished Goods (FG)*"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      disabled={isview}
                    />
                  )}
                  disabled={isview}
                />
              )}
            </Box>
          )}

          {showOtherFields && (
            <>
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <CustomInput
                  value={fields?.name || ""}
                  onChange={(e) =>
                    setFields({ ...fields, name: e.target.value })
                  }
                  label="Name*"
                  disabled={isview}
                />
              </Box>
            </>
          )}
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <CustomInput
              value={fields?.link || ""}
              onChange={(e) => setFields({ ...fields, link: e.target.value })}
              label="Link"
            />
          </Box>

          {/* Quantity */}
          <Box sx={{ flex: 1, minWidth: 100 }}>
            <CustomInput
              disabled={loading || isview}
              value={fields?.quantity || ""}
              onChange={(e) =>
                setFields({ ...fields, err: "", quantity: e.target.value })
              }
              type="number"
              label={"Quantity*"}
            />
          </Box>

          {/* Reason */}
          <Box sx={{ flex: 1, minWidth: 100 }}>
            <CustomInput
              disabled={loading || isview}
              value={fields?.reason || ""}
              onChange={(e) =>
                setFields({ ...fields, err: "", reason: e.target.value })
              }
              type="text"
              label={"Reason*"}
            />
          </Box>

          {/* Delete Button for Default Fields */}
          {!isview && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                color="error"
                onClick={handleRemoveDefaultItem}
                aria-label="delete"
                size="large"
                disabled={isDeleteDisabled}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* ADDITIONAL ITEMS */}
        {fields?.additionalItems?.map((item, index) => (
          <Box
            key={index}
            sx={{
              mt: 1,
              p: 2,
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {/* Material Type */}
            <Box sx={{ flex: 1, minWidth: 150 }}>
              <Autocomplete
                disableClearable
                options={materialtype}
                value={
                  materialtype.find((mt) => mt._id === item?.materialType) ||
                  null
                }
                onChange={(e, newVal) => {
                  const selectedType = newVal ? newVal._id : null;

                  const updatedItems = fields.additionalItems.map((item, i) => {
                    if (i >= index) {
                      return {
                        ...item,
                        materialType: selectedType,
                        materialId: null,
                        materialName: null,
                        materialname: null,
                        showOtherFields: false,
                      };
                    }

                    return item;
                  });

                  setFields({
                    ...fields,
                    additionalItems: updatedItems,
                  });
                }}
                sx={{ width: "100%" }}
                renderInput={(params) => (
                  <CustomInput
                    placeholder="Select Material Type*"
                    {...params}
                    label="Select Material Type*"
                    margin="dense"
                    disabled={isview}
                  />
                )}
                disabled={isview}
              />
            </Box>

            {/* Material Selection */}
            {item?.materialType && (
              <Box sx={{ flex: 1, minWidth: 150 }}>
                {item?.materialType == 1 && (
                  <AsyncDropDown
                    Value={
                      item?.materialName
                        ? {
                            _id: item?.materialId,
                            name: item?.materialName,
                          }
                        : null
                    }
                    defaultOptions={[{ name: "Other", _id: "other" }]}
                    lazyFun={async (para) =>
                      await fetchRawMaterialApi({ ...para, allStatus: true })
                    }
                    OptionComponent={({ option, ...rest }) => {
                      return (
                        <ListItem {...rest}>
                          {option?.name} {option?.mpn ? `[${option?.mpn}]` : ""}
                        </ListItem>
                      );
                    }}
                    onChange={async (changedVal) => {
                      const isOther = changedVal && changedVal._id === "other";
                      updateAdditionalItem(index, {
                        materialId: changedVal ? changedVal?._id : null,
                        materialName: changedVal
                          ? `${changedVal?.name ?? ""}${changedVal?.mpn ? ` [${changedVal?.mpn}]` : ""}`.trim()
                          : null,
                        showOtherFields: isOther,
                      });
                    }}
                    titleKey={"name"}
                    valueKey={"_id"}
                    InputComponent={(params) => (
                      <CustomInput
                        {...params}
                        label="Select Raw Material*"
                        placeholder="Select Raw Material*"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                        disabled={isview}
                      />
                    )}
                    disabled={isview}
                  />
                )}

                {item?.materialType === 2 && (
                  <AsyncDropDown
                    defaultVal={
                      item?.materialId
                        ? {
                            _id: item?.materialId,
                            name: item?.materialname,
                          }
                        : null
                    }
                    defaultOptions={[{ name: "Other", _id: "other" }]}
                    lazyFun={async (para) =>
                      await FetchSKDApi({ ...para, allStatus: true })
                    }
                    OptionComponent={({ option, ...rest }) => {
                      return <ListItem {...rest}>{option?.name}</ListItem>;
                    }}
                    onChange={async (changedVal) => {
                      const isOther = changedVal && changedVal._id === "other";
                      updateAdditionalItem(index, {
                        materialId: changedVal ? changedVal?._id : null,
                        materialname: changedVal ? changedVal?.name : null,
                        showOtherFields: isOther,
                      });
                    }}
                    titleKey={"name"}
                    valueKey={"_id"}
                    InputComponent={(params) => (
                      <CustomInput
                        {...params}
                        label="Select Semi-Knocked Down (SKD)*"
                        placeholder="Select Semi-Knocked Down (SKD)*"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                        disabled={isview}
                      />
                    )}
                    disabled={isview}
                  />
                )}

                {item?.materialType === 3 && (
                  <AsyncDropDown
                    Value={
                      item?.materialName
                        ? {
                            _id: item?.materialId,
                            name: item?.materialName,
                          }
                        : null
                    }
                    defaultOptions={[{ name: "Other", _id: "other" }]}
                    lazyFun={async (para) =>
                      await FetchSFGApi({ ...para, allStatus: true })
                    }
                    OptionComponent={({ option, ...rest }) => {
                      return (
                        <ListItem {...rest}>
                          {option?.name} {option?.mpn ? `(${option?.mpn})` : ""}
                        </ListItem>
                      );
                    }}
                    onChange={async (changedVal) => {
                      const isOther = changedVal && changedVal._id === "other";
                      updateAdditionalItem(index, {
                        materialId: changedVal ? changedVal?._id : null,
                        materialName: changedVal
                          ? `${changedVal?.name ?? ""}${changedVal?.mpn ? ` (${changedVal?.mpn})` : ""}`.trim()
                          : null,
                        showOtherFields: isOther,
                      });
                    }}
                    titleKey={"name"}
                    valueKey={"_id"}
                    InputComponent={(params) => (
                      <CustomInput
                        {...params}
                        label="Select Semi-Finished Goods (SFG)*"
                        placeholder="Select Semi-Finished Goods (SFG)*"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                        disabled={isview}
                      />
                    )}
                    disabled={isview}
                  />
                )}

                {item?.materialType == 4 && (
                  <AsyncDropDown
                    defaultVal={
                      item?.materialId
                        ? {
                            _id: item?.materialId,
                            name: item?.materialname,
                          }
                        : null
                    }
                    defaultOptions={[{ name: "Other", _id: "other" }]}
                    lazyFun={async (para) =>
                      await FetchFGApi({ ...para, allStatus: true })
                    }
                    OptionComponent={({ option, ...rest }) => {
                      return <ListItem {...rest}>{option?.name}</ListItem>;
                    }}
                    onChange={async (changedVal) => {
                      const isOther = changedVal && changedVal._id === "other";
                      updateAdditionalItem(index, {
                        materialId: changedVal ? changedVal?._id : null,
                        materialname: changedVal ? changedVal?.name : null,
                        showOtherFields: isOther,
                      });
                    }}
                    titleKey={"name"}
                    valueKey={"_id"}
                    InputComponent={(params) => (
                      <CustomInput
                        {...params}
                        label="Select Finished Goods (FG)*"
                        placeholder="Select Finished Goods (FG)*"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                        disabled={isview}
                      />
                    )}
                    disabled={isview}
                  />
                )}
              </Box>
            )}

            {item?.showOtherFields && (
              <>
                <Box sx={{ flex: 1, minWidth: 120 }}>
                  <CustomInput
                    value={item?.name || ""}
                    onChange={(e) =>
                      updateAdditionalItem(index, { name: e.target.value })
                    }
                    label="Name"
                    disabled={isview}
                  />
                </Box>
              </>
            )}
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <CustomInput
                value={item?.link || ""}
                onChange={(e) =>
                  updateAdditionalItem(index, { link: e.target.value })
                }
                label="Link"
              />
            </Box>

            {/* Quantity */}
            <Box sx={{ flex: 1, minWidth: 100 }}>
              <CustomInput
                disabled={loading || isview}
                value={item?.quantity || ""}
                onChange={(e) =>
                  updateAdditionalItem(index, { quantity: e.target.value })
                }
                type="number"
                label={"Quantity*"}
              />
            </Box>

            {/* Reason */}
            <Box sx={{ flex: 1, minWidth: 100 }}>
              <CustomInput
                disabled={loading || isview}
                value={item?.reason || ""}
                onChange={(e) =>
                  updateAdditionalItem(index, { reason: e.target.value })
                }
                type="text"
                label={"Reason*"}
              />
            </Box>

            {/* Delete Button for Additional Items */}
            {!isview && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  color="error"
                  onClick={() => handleRemoveItem(index)}
                  aria-label="delete"
                  size="large"
                  disabled={isDeleteDisabled}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        ))}

        {/* Add Button */}
        {!isview && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              mt: 2,
            }}
          >
            <IconButton
              sx={{ width: "9%" }}
              disabled={loading}
              size="inherit"
              onClick={handleAddItem}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "50%",
                  border: "2px solid",
                }}
              >
                <AddIcon sx={{ width: "100%", height: "4vh" }} />
              </Box>
            </IconButton>
          </Box>
        )}
      </Box>
    </CustomDialog>
  );
};

export default PurchaseRequestCreateUi;
